import { useState } from 'react';
import { useEmployeeUser } from './EmployeeUserContext';
import { hrProfileService } from '@/lib/hrProfileService';
import {
  Clock, CheckCircle2, Building2, AlertCircle, Loader2, Briefcase,
} from 'lucide-react';

export function EmployeeApprovalsPage() {
  const { session, seedSession } = useEmployeeUser();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<string | null>(null);

  if (!session) return null;

  const pending = session.employments.filter(e => e.status === 'pending_approval');

  const handleApprove = async (employmentId: string) => {
    setLoadingId(employmentId);
    setError(null);
    const { error: err } = await hrProfileService.approveEmployment(employmentId);
    setLoadingId(null);
    if (err) {
      setError(err);
      return;
    }
    setSuccessId(employmentId);

    const updatedEmployments = session.employments.map(e =>
      e.id === employmentId
        ? { ...e, status: 'active' as const, approvedAt: new Date().toISOString() }
        : e,
    );
    seedSession({ ...session, employments: updatedEmployments });

    setTimeout(() => setSuccessId(null), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
          <Clock className="w-7 h-7 text-amber-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">בקשות ממתינות</h1>
          <p className="text-sm text-gray-500">
            {pending.length > 0
              ? `${pending.length} חברות מחכות לאישור שלך`
              : 'אין בקשות ממתינות'}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {successId && (
        <div className="flex items-center gap-2 px-3.5 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>ההצטרפות אושרה בהצלחה!</span>
        </div>
      )}

      {pending.length === 0 && !successId && (
        <div className="text-center py-16">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">אין בקשות ממתינות</p>
          <p className="text-gray-400 text-xs mt-1">כל ההזמנות אושרו</p>
        </div>
      )}

      <div className="space-y-4">
        {pending.map(emp => {
          const isLoading = loadingId === emp.id;
          const isSuccess = successId === emp.id;

          return (
            <div key={emp.id}
              className={[
                'rounded-2xl border p-5 space-y-4 transition-colors',
                isSuccess
                  ? 'bg-green-50 border-green-200'
                  : 'bg-white border-gray-200',
              ].join(' ')}
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <Building2 className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">{emp.companyName || 'חברה'}</p>
                  {emp.jobTitle && (
                    <p className="text-sm text-gray-600 mt-0.5">תפקיד: {emp.jobTitle}</p>
                  )}
                  {emp.invitedAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      הוזמנת ב-{new Date(emp.invitedAt).toLocaleDateString('he-IL')}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-sm text-amber-800">
                  חברת <strong>{emp.companyName}</strong> מבקשת ממך להצטרף כעובד.
                  באישור, תוכל להתחיל בתהליך הקליטה.
                </p>
              </div>

              {isSuccess ? (
                <div className="flex items-center justify-center gap-2 py-3 text-green-700 font-semibold text-sm">
                  <CheckCircle2 className="w-5 h-5" />
                  אושר בהצלחה!
                </div>
              ) : (
                <button
                  onClick={() => handleApprove(emp.id)}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                             bg-teal-600 hover:bg-teal-700 active:bg-teal-800
                             text-white font-semibold text-sm
                             disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <CheckCircle2 className="w-4 h-4" />
                  }
                  {isLoading ? 'מאשר...' : 'אשר הצטרפות לחברה'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="h-4" />
    </div>
  );
}
